/**
 * 全局 AI 调用记录 /llm-request-logs（旧 views/system/GlobalLLMLogsView.vue）：
 * 统计卡片、按用户 / 日期 / 供应商 / 模型 / 用途筛选、分页（页码同步到地址栏）
 */
import { useEffect, useState } from 'react'
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Statistic
} from 'antd'
import {
  ApiOutlined,
  CloudOutlined,
  DollarOutlined,
  FileTextOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { getRouteApi, useNavigate, useRouter } from '@tanstack/react-router'
import dayjs, { type Dayjs } from 'dayjs'
import { PageHeader } from '@/components/PageHeader'
import { useGlobalLLMLogs } from './hooks'
import { LLMLogCard } from './LLMLogCard'
import type { GlobalLLMLogsFilters } from './types'
import styles from './LLMLogsPage.module.css'

const routeApi = getRouteApi('/admin/llm-request-logs')

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' },
  { value: 'xai', label: 'XAI' },
  { value: 'cerebras', label: 'Cerebras' },
  { value: 'deepseek', label: 'DeepSeek' }
]

const PURPOSE_OPTIONS = [
  { value: 'content_generation', label: '内容生成' },
  { value: 'template_execution', label: '模板执行' },
  { value: 'chat', label: '对话' },
  { value: 'analysis', label: '分析' }
]

type DateRange = [Dayjs, Dayjs]

// 日期预设在打开页面时计算，与旧版一致
function rangePresets(): { label: string; value: DateRange }[] {
  const now = dayjs()
  const yesterday = now.subtract(1, 'day')
  const lastMonth = now.subtract(1, 'month')
  return [
    { label: '今天', value: [now.startOf('day'), now.endOf('day')] },
    { label: '昨天', value: [yesterday.startOf('day'), yesterday.endOf('day')] },
    { label: '最近7天', value: [now.subtract(7, 'day'), now] },
    { label: '最近30天', value: [now.subtract(30, 'day'), now] },
    { label: '本月', value: [now.startOf('month'), now.endOf('month')] },
    { label: '上月', value: [lastMonth.startOf('month'), lastMonth.endOf('month')] }
  ]
}

interface DraftFilters {
  userId: number | null
  dateRange: DateRange | null
  provider?: string
  model: string
  purpose?: string
}

const EMPTY_DRAFT: DraftFilters = { userId: null, dateRange: null, model: '' }

// 表单值转成后端查询参数，空值不传
function toQueryFilters(draft: DraftFilters): GlobalLLMLogsFilters {
  const filters: GlobalLLMLogsFilters = {}
  if (draft.userId !== null) filters.user_id = draft.userId
  if (draft.dateRange) {
    filters.start_date = draft.dateRange[0].format('YYYY-MM-DD')
    filters.end_date = draft.dateRange[1].format('YYYY-MM-DD')
  }
  if (draft.provider) filters.provider = draft.provider
  if (draft.model.trim()) filters.model = draft.model.trim()
  if (draft.purpose) filters.purpose = draft.purpose
  return filters
}

export function GlobalLLMLogsPage() {
  const { message } = App.useApp()
  const router = useRouter()
  const navigate = useNavigate()
  const page = routeApi.useSearch({ select: (search) => search.page ?? 1 })
  const [pageSize, setPageSize] = useState(20)
  const [showFilters, setShowFilters] = useState(false)
  const [draft, setDraft] = useState<DraftFilters>(EMPTY_DRAFT)
  const [filters, setFilters] = useState<GlobalLLMLogsFilters>({})
  const [presets] = useState(rangePresets)
  const { data, isFetching, isError, refetch } = useGlobalLLMLogs(page, pageSize, filters)

  useEffect(() => {
    if (isError) message.error('获取LLM日志失败')
  }, [isError, message])

  const logs = data?.logs ?? []
  const total = data?.total ?? 0
  const pageTokens = logs.reduce((sum, log) => sum + (log.total_tokens ?? 0), 0)
  const pageCost = logs.reduce((sum, log) => sum + (log.total_cost ?? 0), 0)

  // 页码写进地址栏，刷新页面后保持
  const goToPage = (nextPage: number) => {
    navigate({ to: '/llm-request-logs', search: { page: nextPage }, replace: true }).catch(console.error)
  }

  const applyFilters = () => {
    setFilters(toQueryFilters(draft))
    goToPage(1)
  }

  const resetFilters = () => {
    setDraft(EMPTY_DRAFT)
    setFilters({})
    goToPage(1)
  }

  const changeDraft = <K extends keyof DraftFilters>(key: K, value: DraftFilters[K]) => {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  let content = (
    <div>
      {logs.map((log) => (
        <LLMLogCard key={log.id} log={log} showUserInfo />
      ))}
      {total > 0 && (
        <div className={styles.pagination}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            showQuickJumper
            showTotal={(count) => `共 ${count} 条记录`}
            pageSizeOptions={[10, 20, 50, 100]}
            onChange={(nextPage, nextPageSize) => {
              setPageSize(nextPageSize)
              goToPage(nextPage)
            }}
          />
        </div>
      )}
    </div>
  )
  if (isFetching && logs.length === 0) {
    content = (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    )
  } else if (logs.length === 0) {
    content = (
      <Empty description="暂无AI调用记录">
        <Button type="primary" icon={<ReloadOutlined />} onClick={() => refetch()}>
          刷新数据
        </Button>
      </Empty>
    )
  }

  return (
    <div>
      <PageHeader
        title="全局AI调用记录"
        onBack={() => router.history.back()}
        extra={
          <Space>
            <Button
              type={showFilters ? 'primary' : 'default'}
              icon={<FilterOutlined />}
              onClick={() => setShowFilters((current) => !current)}
            >
              筛选{showFilters ? '（已展开）' : ''}
            </Button>
            <Button type="primary" icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
              刷新
            </Button>
          </Space>
        }
      />

      <Row gutter={[16, 16]} className={styles.stats}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="总调用次数" value={total} prefix={<ApiOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="当前页记录" value={logs.length} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="本页Token消耗" value={pageTokens} suffix="tokens" prefix={<CloudOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="本页成本估算" value={pageCost} precision={4} prefix={<DollarOutlined />} />
          </Card>
        </Col>
      </Row>

      {showFilters && (
        <Card className={styles.filters}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={6}>
              <div className={styles.filterLabel}>用户ID：</div>
              <InputNumber
                aria-label="用户ID"
                placeholder="输入用户ID"
                style={{ width: '100%' }}
                value={draft.userId}
                onChange={(value) => changeDraft('userId', value)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className={styles.filterLabel}>日期范围：</div>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                presets={presets}
                value={draft.dateRange}
                onChange={(value) => changeDraft('dateRange', value?.[0] && value[1] ? [value[0], value[1]] : null)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className={styles.filterLabel}>供应商：</div>
              <Select
                aria-label="供应商"
                placeholder="选择供应商"
                allowClear
                style={{ width: '100%' }}
                value={draft.provider}
                options={PROVIDER_OPTIONS}
                onChange={(value) => changeDraft('provider', value)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className={styles.filterLabel}>模型：</div>
              <Input
                aria-label="模型"
                placeholder="输入模型名称"
                value={draft.model}
                onChange={(event) => changeDraft('model', event.target.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className={styles.filterLabel}>用途：</div>
              <Select
                aria-label="用途"
                placeholder="选择用途"
                allowClear
                style={{ width: '100%' }}
                value={draft.purpose}
                options={PURPOSE_OPTIONS}
                onChange={(value) => changeDraft('purpose', value)}
              />
            </Col>
            <Col xs={24} sm={12} md={18} className={styles.filterActions}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} loading={isFetching} onClick={applyFilters}>
                  应用筛选
                </Button>
                <Button onClick={resetFilters}>重置</Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {content}
    </div>
  )
}
